import React, { useState } from "react";
import { FormProvider } from "react-hook-form";
import { Button, HorizontalStepper } from "@dune/ui";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
	ConstructionSiteDetailsFormData,
	useConstructionSiteDetailsForm,
} from "../../../component/brokers/constructionSites/useConstructionSiteDetailsForm";
import { useSubmitPublicConstructionSiteQuestionnaire } from "../../../api/mutations/constructionSites.mutations";
import AddressDetailsContainer from "../../../component/brokers/constructionSites/AddressDetailsContainer";
import ActionsDetailsContainer from "../../../component/brokers/constructionSites/ActionsDetailsContainer";
import DatesDetailsContainer from "../../../component/brokers/constructionSites/DatesDetailsContainer";
import InterventionsDetailsContainer from "../../../component/brokers/constructionSites/InterventionsDetailsContainer";
import DeclarationsContainer from "../../../component/brokers/constructionSites/DeclarationsContainer";
import DetailsDescriptionContainer from "../../../component/brokers/constructionSites/DetailsDescriptionContainer";

// Schémas de validation par étape (simplifiés pour l'instant)
// Ces schémas seront utilisés dans le futur pour une validation plus robuste

// Fonction pour obtenir les champs à valider par étape
const getFieldsForStep = (
	step: number,
): (keyof ConstructionSiteDetailsFormData)[] => {
	switch (step) {
		case 1:
			return ["streetName", "postalCode", "city"];
		case 2:
			return ["actions", "totalAmountTTC"];
		case 3:
			return [
				"startDate",
				"deliveryDate",
				"cityHallStartDate",
				"totalAmountTTC",
			];
		case 4:
			return ["interventions"];
		case 5:
			return ["ccmi", "nonUsualMethods"];
		default:
			return [];
	}
};

interface ConstructionSiteQuizFormProps {
	constructionSiteId: number;
	brokerSociety: {
		id: number;
		denomination: string;
		logo?: string | null;
	};
	token: string;
	onBack: () => void;
}

const ConstructionSiteQuizForm: React.FC<ConstructionSiteQuizFormProps> = ({
	constructionSiteId,
	brokerSociety,
	token,
	onBack,
}) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [isCompleted, setIsCompleted] = useState(false);

	const submitQuestionnaireMutation =
		useSubmitPublicConstructionSiteQuestionnaire();

	// Utiliser le même hook form que ConstructionSiteDetailsForm
	const { form } = useConstructionSiteDetailsForm();
	const { handleSubmit } = form;

	const steps = [
		{ step: 1, title: "Adresse" },
		{ step: 2, title: "Opération" },
		{ step: 3, title: "Dates des travaux" },
		{ step: 4, title: "Intervenants" },
		{ step: 5, title: "Déclarations" },
	];

	// Fonction pour valider une étape spécifique
	const validateStep = async (step: number): Promise<boolean> => {
		const fieldsToValidate = getFieldsForStep(step);
		return await form.trigger(fieldsToValidate);
	};

	// Fonction pour vérifier si l'étape actuelle est valide (pour l'état des boutons)
	const isCurrentStepValid = (): boolean => {
		const values = form.getValues();

		switch (currentStep) {
			case 1: {
				// Adresse
				return !!(values.streetName && values.postalCode && values.city);
			}
			case 2: {
				// Opération
				if (!values.actions?.types?.length || !values.actions?.buildingType) {
					return false;
				}
				// Vérifier le montant total
				if (!values.totalAmountTTC || values.totalAmountTTC <= 0) {
					return false;
				}
				return true;
			}
			case 3: {
				// Dates
				return !!(
					values.startDate &&
					values.deliveryDate &&
					values.totalAmountTTC &&
					values.totalAmountTTC > 0
				);
			}
			case 4: {
				// Intervenants
				const interventions = values.interventions;
				if (!interventions) return false;

				const hasAnyIntervention = Object.values(interventions).some(
					(category) => Object.values(category).some(Boolean),
				);
				return hasAnyIntervention;
			}
			case 5: {
				// Déclarations
				return !!(
					values.ccmi !== undefined && values.nonUsualMethods !== undefined
				);
			}
			default:
				return false;
		}
	};

	const onSubmit = async (data: ConstructionSiteDetailsFormData) => {
		const publicData = {
			streetNumber: data.streetNumber,
			streetName: data.streetName,
			postalCode: data.postalCode,
			city: data.city,
			rga: data.rga,
			sismic: data.sismic,
			throughSyndic: data.throughSyndic,
			actions: data.actions,
			totalAmountTTC: data.totalAmountTTC,
			totalAmountHT: data.totalAmountHT,
			startDate: new Date(data.startDate),
			deliveryDate: new Date(data.deliveryDate),
			cityHallStartDate: data.cityHallStartDate
				? new Date(data.cityHallStartDate)
				: undefined,
			interventions: data.interventions,
			ccmi: data.ccmi,
			nonUsualMethods: data.nonUsualMethods,
			noOtherOperations: data.noOtherOperations,
			projectName: data.projectName,
			operationType: data.operationType,
			processesType: data.processesType,
		};

		// Utiliser la mutation React Query
		submitQuestionnaireMutation.mutate(
			{
				constructionSiteId,
				token,
				questionnaireData: publicData,
			},
			{
				onSuccess: () => {
					setIsCompleted(true);
				},
			},
		);
	};

	const handleNext = async () => {
		const isValid = await validateStep(currentStep);

		if (isValid && currentStep < steps.length) {
			setCurrentStep(currentStep + 1);
		}
		// Les erreurs de validation sont maintenant affichées automatiquement
		// par react-hook-form à côté des champs correspondants
	};

	const handlePrevious = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return <AddressDetailsContainer />;
			case 2:
				return <ActionsDetailsContainer hideComplementaryButton={true} />;
			case 3:
				return <DatesDetailsContainer isDisabled={false} />;
			case 4:
				return <InterventionsDetailsContainer isDisabled={false} />;
			case 5:
				return <DeclarationsContainer isDisabled={false} />;
			default:
				return null;
		}
	};

	if (isCompleted) {
		return (
			<div className="min-h-screen bg-white">
				<div className="bg-white px-6 py-4">
					<div className="max-w-4xl mx-auto">
						<div className="text-center mb-6">
							{brokerSociety.logo ? (
								<img
									src={brokerSociety.logo}
									alt={`Logo ${brokerSociety.denomination}`}
									className="h-16 mx-auto object-contain"
								/>
							) : (
								<div className="h-16 flex items-center justify-center text-gray-400">
									Aucun logo disponible
								</div>
							)}
						</div>

						<HorizontalStepper
							steps={steps}
							currentStepIndex={6}
							setCurrentStepIndex={() => {}}
							className="pointer-events-none"
							showSeparator={false}
						/>
					</div>
				</div>

				<div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
					<div className="max-w-2xl mx-auto p-6 text-center">
						<h1 className="text-3xl font-medium text-text-main-blue mb-4">
							Merci votre formulaire est bien soumis.
						</h1>
						<p className="text-sm text-gray-600 mb-8">
							Notre équipe étudie celui-ci et reviens vers vous dans les plus
							brefs délais.
						</p>
						<Button onClick={onBack} variant="outline">
							Fermer
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<FormProvider {...form}>
			<div className="min-h-screen bg-white">
				<div className="bg-white px-6 py-4">
					<div className="max-w-7xl mx-auto">
						<Button
							className="self-start text-[#144C65] px-0 has-[>svg]:px-0 mb-4"
							variant="link"
							size="sm"
							onClick={onBack}
						>
							<ArrowLeft className="h-4 w-4 mr-2" />
							Retour
						</Button>

						<div className="text-center mb-6">
							{brokerSociety.logo ? (
								<img
									src={brokerSociety.logo}
									alt={`Logo ${brokerSociety.denomination}`}
									className="h-16 mx-auto object-contain"
								/>
							) : (
								<img
									src="/dune_logo.png"
									alt="Dune Logo"
									className="h-16 mx-auto object-contain"
								/>
							)}
						</div>

						<HorizontalStepper
							steps={steps}
							currentStepIndex={currentStep}
							setCurrentStepIndex={(step) => {
								if (
									step < currentStep ||
									(step === currentStep + 1 && isCurrentStepValid())
								) {
									setCurrentStep(step);
								}
							}}
							showSeparator={false}
						/>
					</div>
				</div>

				<div className="flex items-center justify-center p-6 w-full">
					<div className="grid grid-cols-5 gap-4 w-full max-w-7xl">
						<div className="col-span-3">
							<form
								onSubmit={handleSubmit(onSubmit)}
								className="flex flex-col items-center justify-center p-4"
							>
								<div className="flex flex-col gap-6 w-full">
									<h2 className="text-[#144C65] text-2xl font-medium">
										{steps[currentStep - 1]?.title}
									</h2>

									{renderStepContent()}

									<div className="self-end flex flex-row gap-4">
										{currentStep > 1 && (
											<Button
												type="button"
												variant="outline"
												size="lg"
												onClick={handlePrevious}
											>
												<ArrowLeft className="w-4 h-4 mr-2" />
												Précédent
											</Button>
										)}

										{currentStep === steps.length ? (
											<Button
												type="submit"
												variant="default"
												size="lg"
												disabled={
													submitQuestionnaireMutation.isPending ||
													!isCurrentStepValid()
												}
											>
												{submitQuestionnaireMutation.isPending
													? "Envoi en cours..."
													: "Terminer"}
												{!submitQuestionnaireMutation.isPending && (
													<ArrowRight className="w-4 h-4 ml-2" />
												)}
											</Button>
										) : (
											<Button
												type="button"
												variant="default"
												size="lg"
												onClick={handleNext}
												disabled={!isCurrentStepValid()}
											>
												Suivant
												<ArrowRight className="w-4 h-4 ml-2" />
											</Button>
										)}
									</div>
								</div>
							</form>
						</div>
						<div className="col-span-2">
							<DetailsDescriptionContainer />
						</div>
					</div>
				</div>
			</div>
		</FormProvider>
	);
};

export default ConstructionSiteQuizForm;
